export const INTERNAL_TEST_CONTRACT_VERSION = 'staging-secure-pilot-v1';

export const PILOT_EVENT_NAMES = [
  'pilot_slot_activated',
  'session_started',
  'session_ended',
  'consent_presented',
  'consent_granted',
  'consent_declined',
  'consent_withdrawn',
  'flow_search_started',
  'flow_search_finished',
  'flow_qa_started',
  'flow_qa_finished',
  'flow_correction_started',
  'flow_correction_finished',
  'flow_paper_analysis_started',
  'flow_paper_analysis_finished',
  'flow_plan_recommendation_started',
  'flow_plan_recommendation_finished',
  'feedback_submitted',
  'incident_opened',
  'incident_resolved',
] as const;

export type PilotEventName = (typeof PILOT_EVENT_NAMES)[number];
export type PilotEventStatus =
  | 'success'
  | 'presented'
  | 'granted'
  | 'declined'
  | 'withdrawn'
  | 'started'
  | 'passed'
  | 'failed'
  | 'skipped'
  | 'open'
  | 'resolved';
export type PilotFlowName = 'search' | 'qa' | 'correction' | 'paper_analysis' | 'plan_recommendation';
export type PilotErrorCategory =
  | 'auth'
  | 'network'
  | 'timeout'
  | 'upstream'
  | 'validation'
  | 'storage'
  | 'unknown_redacted';
export type PilotDeviceClass = 'android_phone' | 'android_tablet' | 'unknown';

export type PilotEvent = {
  event_id: string;
  client_event_id?: string;
  session_id?: string;
  event_name: PilotEventName;
  event_status: PilotEventStatus;
  flow_name?: PilotFlowName;
  error_category?: PilotErrorCategory;
  duration_ms?: number;
  occurred_day: number;
  event_at: string;
  app_version: string;
  device_class: PilotDeviceClass;
  consent_version?: string;
};

type CreatePilotEventInput = {
  eventId: string;
  clientEventId?: string;
  sessionId?: string;
  eventName: string;
  eventStatus: PilotEventStatus;
  flowName?: PilotFlowName;
  errorCategory?: PilotErrorCategory;
  durationMs?: number;
  occurredDay: number;
  eventAt: string;
  appVersion: string;
  deviceClass: PilotDeviceClass;
  consentVersion?: string;
};

const eventNameSet = new Set<string>(PILOT_EVENT_NAMES);
const lifecycleStatuses: Partial<Record<PilotEventName, PilotEventStatus[]>> = {
  pilot_slot_activated: ['success'],
  session_started: ['success'],
  session_ended: ['success'],
  consent_presented: ['presented'],
  consent_granted: ['granted'],
  consent_declined: ['declined'],
  consent_withdrawn: ['withdrawn'],
  feedback_submitted: ['success'],
  incident_opened: ['open'],
  incident_resolved: ['resolved'],
};

const expectedFlowForEvent = (eventName: PilotEventName): PilotFlowName | null => {
  if (!eventName.startsWith('flow_')) return null;
  return eventName.replace(/^flow_/, '').replace(/_(started|finished)$/, '') as PilotFlowName;
};

export const createPilotEvent = ({
  eventId,
  clientEventId,
  sessionId,
  eventName,
  eventStatus,
  flowName,
  errorCategory,
  durationMs,
  occurredDay,
  eventAt,
  appVersion,
  deviceClass,
  consentVersion,
}: CreatePilotEventInput): PilotEvent => {
  if (!eventNameSet.has(eventName)) throw new Error('Unsupported pilot event name');
  const typedEventName = eventName as PilotEventName;
  const expectedFlow = expectedFlowForEvent(typedEventName);
  const allowedStatuses = expectedFlow
    ? typedEventName.endsWith('_started')
      ? ['started']
      : ['passed', 'failed', 'skipped']
    : lifecycleStatuses[typedEventName] ?? [];
  if (!allowedStatuses.includes(eventStatus)) throw new Error('Pilot event status does not match event name');
  if (expectedFlow !== (flowName ?? null)) throw new Error('Pilot flow name does not match event name');
  if (eventStatus === 'failed' && !errorCategory) throw new Error('Failed pilot events require an error category');
  if (!Number.isInteger(occurredDay) || occurredDay < 0 || occurredDay > 7) throw new Error('Pilot occurred day is out of range');
  if (durationMs !== undefined && (!Number.isInteger(durationMs) || durationMs < 0 || durationMs > 3_600_000)) {
    throw new Error('Pilot event duration is out of range');
  }

  return {
    event_id: eventId,
    ...(clientEventId ? { client_event_id: clientEventId } : {}),
    ...(sessionId ? { session_id: sessionId } : {}),
    event_name: typedEventName,
    event_status: eventStatus,
    ...(flowName ? { flow_name: flowName } : {}),
    ...(errorCategory ? { error_category: errorCategory } : {}),
    ...(durationMs !== undefined ? { duration_ms: durationMs } : {}),
    occurred_day: occurredDay,
    event_at: eventAt,
    app_version: appVersion,
    device_class: deviceClass,
    ...(consentVersion ? { consent_version: consentVersion } : {}),
  };
};

export const summarizePilotEventForDebug = (event: PilotEvent) => ({
  eventId: event.event_id,
  eventName: event.event_name,
  eventStatus: event.event_status,
});
